import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check, Factory, Cog, HeartPulse, ShoppingBag, Building2, Users2 } from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";

const STORIES = [
  {
    icon: Factory,
    industry: "Manufacturing",
    situation: "Rapid business growth created operational complexity across multiple locations.",
    challenge: "Existing systems and processes were no longer supporting expansion, and leadership lacked a single, board-level view of where the operation was straining.",
    approach: "Worked with the executive team to develop a business-led technology roadmap, strengthen governance and prioritise the initiatives most aligned with the growth agenda.",
    outcome: "Greater operational visibility, sharper decision-making at the top table and a scalable foundation for the next stage of growth.",
    lesson: "Technology investments create the most value when they follow the business strategy, not the other way around.",
  },
  {
    icon: Cog,
    industry: "Engineering",
    situation: "Multiple disconnected systems reduced collaboration across teams and weakened executive reporting.",
    challenge: "Leadership lacked reliable visibility of business performance, and decisions were being made on partial information.",
    approach: "Established a lean governance model, simplified how technology decisions were made and re-aligned technology priorities with operational objectives.",
    outcome: "Improved executive visibility, stronger operational control and clearer accountability across the leadership team.",
    lesson: "Good governance often creates more business value than adding new technology.",
  },
  {
    icon: HeartPulse,
    industry: "Healthcare",
    situation: "Growth had increased operational complexity while compliance expectations were rising in parallel.",
    challenge: "Leadership needed to balance the pursuit of efficiency with its regulatory responsibilities, without compromising either.",
    approach: "Focused on strengthening governance, simplifying operations and giving the executive team a clearer, more direct set of reporting lines.",
    outcome: "Improved business resilience, stronger executive confidence and a more defensible position with regulators and stakeholders.",
    lesson: "Sustainable growth in regulated sectors depends on disciplined governance from the top down.",
  },
  {
    icon: ShoppingBag,
    industry: "Retail & Distribution",
    situation: "Shifting customer expectations were placing pressure on operations and squeezing margin.",
    challenge: "The business needed to become more responsive to the customer while protecting profitability, not one at the expense of the other.",
    approach: "Reviewed the core business processes, tightened technology priorities against them and rebuilt the executive reporting around what actually mattered.",
    outcome: "Improved operational agility, a stronger customer experience and a leaner cost base that supported growth.",
    lesson: "Customer experience is won or lost in the operations behind it, long before it reaches the customer.",
  },
  {
    icon: Building2,
    industry: "Industrial Business",
    situation: "Expansion into new sites required stronger coordination across departments that had grown up largely independent of each other.",
    challenge: "Technology decisions had become reactive rather than strategic, and were being made in isolation from the wider business plan.",
    approach: "Developed an executive technology roadmap anchored in the long-term business goals, and gave leadership a clear order of priorities to act on.",
    outcome: "Stronger alignment between business strategy and technology investment, and a materially better use of capital and management attention.",
    lesson: "Leadership clarity is the fastest way to reduce technology complexity.",
  },
  {
    icon: Users2,
    industry: "Family-Owned Business",
    situation: "The business was preparing for its next stage of growth, and technology was becoming a more consequential leadership question than it had ever been.",
    challenge: "Leadership needed experienced technology guidance without adding another full-time executive to a lean, tightly held leadership team.",
    approach: "Provided Fractional CIO leadership, established a light-touch governance model and worked with the family and executive team on a practical strategic plan.",
    outcome: "Improved leadership confidence, better investment decisions and a defensible roadmap for the future of the business.",
    lesson: "Growing businesses benefit most from experienced executive guidance at the critical stages of expansion, precisely when the wrong decision costs the most.",
  },
];

const OUTCOMES = [
  { title: "Business Growth", note: "New revenue, markets and capacity as the business scales." },
  { title: "Operational Excellence", note: "A leaner, faster and more resilient operating core." },
  { title: "Investment Optimisation", note: "Technology spend directed to a defensible business return." },
  { title: "Executive Visibility", note: "A clear, board-level view of value, cost and business risk." },
  { title: "Improved Governance", note: "Decisions held to account through a disciplined operating model." },
  { title: "Business Agility", note: "The confidence and capacity to move as the market moves." },
  { title: "Risk Reduction", note: "Cyber, delivery and vendor risk brought under control." },
  { title: "Scalable Operations", note: "Systems built to carry the next stage of growth." },
  { title: "Better Leadership Decisions", note: "The clarity to decide with confidence, and at pace." },
  { title: "Competitive Advantage", note: "Capability that competitors cannot easily copy." },
];

const COMMON = [
  { title: "Understanding the business comes first", body: "Every engagement begins with the strategy, the market and the outcomes leadership is accountable for." },
  { title: "Technology is never the starting point", body: "It is the enabler, never the reason. The business defines what the technology must serve." },
  { title: "Business priorities set the direction", body: "The two or three decisions that most shape the next stage of the business come before everything else." },
  { title: "Leadership alignment follows", body: "The executive team moves as one, behind a single direction that everyone can defend." },
  { title: "Technology enables execution", body: "Systems, platforms and partners are chosen to deliver the plan, not to define it." },
  { title: "Measurable outcomes define success", body: "Progress is judged in the board's own terms: growth, margin, resilience and risk." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Executive Success Stories",
  serviceType: "Executive Technology Leadership Case Examples",
  description: "Representative business situations and executive approaches that demonstrate how strategic technology leadership creates measurable business value for growing businesses across India, the UAE and the Gulf Region.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/case-studies",
};

export default function CaseStudies() {
  return (
    <>
      <Seo
        title="Executive Success Stories"
        description="Representative business situations that demonstrate how experienced executive technology leadership creates measurable business value for growing businesses, family-owned enterprises and mid-market organisations across India, the UAE and the Gulf Region."
        path="/case-studies"
        keywords={["Executive Success Stories", "Executive Technology Leadership", "Fractional CIO", "Executive Technology Advisor", "Business Transformation", "Family-Owned Business Advisor", "Mid-Market Technology Leadership"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="stories-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Executive Success Stories</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Executive success stories,", "built on better"]} lineClassName="font-light" />
            <KineticHeading lines={["business decisions."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            Every growing business faces moments where technology decisions will influence future growth.
            The most successful transformations begin with clear leadership, practical strategy and
            disciplined execution. What follows are representative business situations and executive
            approaches that demonstrate how strategic technology leadership creates long-term business
            value, written to respect client confidentiality.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="stories-hero-discuss"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Discuss Your Business Challenge
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/book" data-testid="stories-hero-book"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Book Executive Strategy Session
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — REPRESENTATIVE BUSINESS STORIES */}
      <section data-testid="stories-list" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Representative business stories</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Real situations,", "written to respect", "confidentiality."]} />
            </h2>
          </div>
          <div className="mt-16 space-y-8">
            {STORIES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.industry} delay={i * 0.04}>
                  <article data-testid={`story-${s.industry.toLowerCase().replace(/[\s&]+/g, "-")}`} className="grid gap-8 rounded-[1.75rem] border border-slate-200 bg-surface p-8 md:grid-cols-12 md:p-12">
                    <div className="md:col-span-7">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                          <Icon className="h-6 w-6" strokeWidth={1.5} />
                        </div>
                        <span className="font-body text-xs font-bold uppercase tracking-[0.2em] text-royal">{s.industry}</span>
                      </div>
                      <div className="mt-6">
                        <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Business situation</p>
                        <h3 className="mt-2 font-heading text-2xl font-bold leading-snug tracking-tight text-navy md:text-3xl">{s.situation}</h3>
                      </div>
                      <div className="mt-6">
                        <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Leadership challenge</p>
                        <p className="mt-2 max-w-xl font-body text-lg leading-relaxed text-slate-600">{s.challenge}</p>
                      </div>
                      <div className="mt-6">
                        <p className="font-body text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Executive approach</p>
                        <p className="mt-2 max-w-xl font-body text-lg leading-relaxed text-slate-600">{s.approach}</p>
                      </div>
                    </div>
                    <div className="md:col-span-5 md:border-l md:border-slate-200 md:pl-10">
                      <div>
                        <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Business outcome</p>
                        <p className="mt-3 font-body text-lg leading-relaxed text-navy">{s.outcome}</p>
                      </div>
                      <div className="mt-8 rounded-[1rem] bg-navy px-6 py-6 text-white">
                        <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-sky">Key leadership lesson</p>
                        <p className="mt-3 font-heading text-lg font-semibold leading-snug tracking-tight">{s.lesson}</p>
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — COMMON BUSINESS OUTCOMES */}
      <section data-testid="stories-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Common business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 lg:grid-cols-5">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-base font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHAT THESE STORIES HAVE IN COMMON */}
      <section data-testid="stories-common" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">What these stories have in common</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The pattern behind", "every engagement."]} />
            </h2>
          </div>
          <div className="mt-16 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {COMMON.map((c, i) => (
              <Reveal key={c.title} className="flex gap-5 border-t border-slate-200 pt-6">
                <span className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-sm font-bold text-white">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{c.title}</h3>
                  <p className="mt-2 max-w-md font-body leading-relaxed text-slate-600">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — EXECUTIVE ADVISORY PHILOSOPHY */}
      <section data-testid="stories-philosophy" className="relative overflow-hidden bg-surface py-24 md:py-32">
        <div className="pointer-events-none absolute right-0 top-1/2 h-[500px] w-[500px] -translate-y-1/2 translate-x-1/3 rounded-full bg-sky/10 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Executive advisory philosophy</p>
          <blockquote className="mt-8 font-heading text-3xl font-light leading-[1.3] tracking-tight text-navy md:text-4xl">
            The objective is never to implement more technology. The objective is to help leadership
            teams make better business decisions, supported by practical technology strategy. That
            philosophy guides every engagement.
          </blockquote>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {["Business first", "Leadership aligned", "Outcomes measured"].map((pill) => (
              <span key={pill} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2 font-body text-sm font-semibold text-navy">
                <Check className="h-4 w-4 text-royal" strokeWidth={2.5} /> {pill}
              </span>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/services/executive-advisory" data-testid="stories-philosophy-cta" className="inline-flex items-center gap-2 font-body text-sm font-semibold text-royal hover:text-navy">
              Explore Executive Advisory <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        overline="Let's write your success story"
        heading={["Let's build your", "next chapter."]}
        body="Every growing business has unique opportunities and challenges. Let's discuss how executive technology leadership can help your organisation build a stronger foundation for sustainable growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
